/**
 * 콘텐츠 등록 제출 시 BE 가 "선택한 채널에서 만들 수 없는 콘텐츠 유형" 을 알리는 에러 코드.
 * 이 코드를 받으면 일반 토스트가 아니라 콘텐츠 URL 인풋의 인라인 에러(빨간 border + 빨간
 * help text) 로 노출한다. (정산 정보 입력 에러 처리와 동일 컨셉)
 */
export const ERROR_CODE_CONTENT_CHANNEL_TYPE_MISMATCH = 'COMMON-001-002';

/**
 * mutation onError 로 들어온 에러가 "채널×콘텐츠 유형 불일치(COMMON-001-002)" 인지 판정한다.
 *
 * non-2xx HTTP 응답만 대상으로 한다. `executeStandardRequest` 가 non-2xx 를
 * `{ data, status, message }` 평문 객체로 reject 하며(`data` = 응답 body 원본), BE errorCode 는
 * 표준 응답이면 `data.meta.errorCode`, 비표준 `{ code, message }` 응답이면 `data.code` 에 담긴다.
 * 큐레이터 시딩 BE 가 엔드포인트별로 두 형태를 혼용하므로 양쪽을 모두 본다. (200 + `meta.result
 * ==='FAIL'` 경로는 service 가 `new Error(message)` 로 throw 하며 errorCode 미보존 → 대상 외.)
 *
 * 콘텐츠 제출 API 는 204 처리를 위해 `responseType: 'text'` 라, 에러 body 가 **파싱 안 된 JSON
 * 문자열**로 `data` 에 담긴다. 따라서 문자열이면 먼저 JSON.parse 한다.
 *
 * NOTE: `@musinsa/services-curator` 의 `isApiError` 와 동일한 판정이지만, 순수 util 이
 * 무거운 service barrel(transitive ESM) 에 결합되지 않도록 [[resolveMutationErrorMessage]]
 * 와 동일하게 동일 shape 의 가드를 인라인한다.
 */
export const isContentChannelTypeMismatchError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('data' in error)) return false;
  let data: unknown = (error as { data?: unknown }).data;
  // responseType:'text' API 는 에러 body 가 JSON 문자열로 온다 → 파싱 후 판정.
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return false;
    }
  }
  if (typeof data !== 'object' || data === null) return false;
  // 비표준 `{ code, message }` 응답.
  if (
    'code' in data &&
    (data as { code?: unknown }).code === ERROR_CODE_CONTENT_CHANNEL_TYPE_MISMATCH
  )
    return true;
  // 표준 응답 `{ data, meta: { errorCode } }`.
  if (!('meta' in data)) return false;
  const { meta } = data as { meta?: unknown };
  if (typeof meta !== 'object' || meta === null || !('errorCode' in meta)) return false;
  const { errorCode } = meta as { errorCode?: unknown };
  return errorCode === ERROR_CODE_CONTENT_CHANNEL_TYPE_MISMATCH;
};
